import { loadFromLocalStorage } from '@databases/localStorage';
import { contantAuthentication } from '@contants/index';
import { useMemo } from 'react';

// Permission constants dựa trên hệ thống GSWM
export const PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'dashboard_view',
  DASHBOARD_EXPORT: 'dashboard_export',

  // Map
  MAP_VIEW: 'map_view',

  // Device Management
  DEVICE_VIEW: 'device_view',
  DEVICE_MODIFY: 'device_modify',
  DEVICE_EXPORT: 'device_export',
  DEVICE_IMPORT: 'device_import',

  // Command Management
  COMMAND_HISTORY_VIEW: 'command_history_view',
  COMMAND_SET_BASIC: 'command_set_basic',
  COMMAND_SET_ADVANCED: 'command_set_advanced',
  COMMAND_VIEW_RESPONSE: 'command_view_response',
  COMMAND_EXPORT: 'command_export',
  COMMAND_VIEW_METER_READING_HISTORY: 'command_view_meter_reading_history',
  COMMAND_SET_METER_READING: 'command_set_meter_reading',
  COMMAND_SET_RETRANSMISSION_TIMES: 'command_set_retransmission_times',
  COMMAND_SET_SERVER_ADDRESS: 'command_set_server_address',
  COMMAND_SET_DENSE_DATA_CYCLE: 'command_set_dense_data_cycle',
  COMMAND_SET_VALVE_CONTROL: 'command_set_valve_control',
  COMMAND_SET_TIME_ZONE: 'command_set_time_zone',
  COMMAND_SET_ALARM_REPORTING: 'command_set_alarm_reporting',
  COMMAND_IMPORT: 'command_import',

  // Replacement Management
  REPLACEMENT_VIEW: 'replacement_view',
  REPLACEMENT_MODIFY: 'replacement_modify',
  REPLACEMENT_EXPORT: 'replacement_export',
  REPLACEMENT_IMPORT: 'replacement_import',

  // Original Data
  ORIGINAL_DATA_VIEW: 'original_data_view',
  ORIGINAL_DATA_EXPORT: 'original_data_export',

  // Consumption Statistics
  CONSUMPTION_VIEW: 'consumption_view',
  CONSUMPTION_CALCULATE: 'consumption_calculate',
  CONSUMPTION_MANUAL_IMPORT: 'consumption_manual_import',
  CONSUMPTION_EXPORT: 'consumption_export',
  CONSUMPTION_ADJUST: 'consumption_adjust',

  // Daily Consumption Statistics
  DAILY_CONSUMPTION_VIEW: 'daily_consumption_view',
  DAILY_CONSUMPTION_EXPORT: 'daily_consumption_export',

  // Warning Management
  WARNING_VIEW: 'warning_view',
  WARNING_UPDATE_STATUS: 'warning_update_status',
  WARNING_EXPORT: 'warning_export',

  // Customer Management
  CUSTOMER_VIEW: 'customer_view',
  CUSTOMER_MODIFY: 'customer_modify',
  CUSTOMER_EXPORT: 'customer_export',
  CUSTOMER_IMPORT: 'customer_import',

  // Configs (Reading Schedule Management)
  CONFIG_VIEW: 'config_view',
  CONFIG_MODIFY: 'config_modify',
  CONFIG_EXPORT: 'config_export',
  CONFIG_IMPORT: 'config_import',

  // Leak Detector Management
  LEAK_DETECTOR_VIEW: 'leak_detector_view',
  LEAK_DETECTOR_EXPORT: 'leak_detector_export',

  // Master Data Management (Management Structure)
  MASTER_DATA_VIEW: 'master_data_view',
  MASTER_DATA_MODIFY: 'master_data_modify',

  // User Management
  USER_VIEW: 'user_view',
  USER_MODIFY: 'user_modify',
  AUTH_MANAGE: 'auth_manage',
  MANAGE_PERMISSIONS: 'manage_permissions',

  // Consumption Monitoring
  CONSUMPTION_MONITORING_VIEW: 'consumption_monitoring_view',
  CONSUMPTION_MONITORING_EXPORT: 'consumption_monitoring_export',

  // Favorite Filter (Bộ lọc theo dõi)
  FAVORITE_FILTER_VIEW: 'favorite_filter_view',
  FAVORITE_FILTER_CREATE: 'favorite_filter_create',
  FAVORITE_FILTER_CREATE_DEVICE: 'favorite_filter_create_device',
  FAVORITE_FILTER_IMPORT: 'favorite_filter_import',

  // DMA Management
  DMA_VIEW: 'dma_view',
  DMA_CREATE: 'dma_create',
  DMA_MODIFY: 'dma_modify',
  DMA_DELETE: 'dma_delete',

  // DMA Monitoring
  DMA_MONITORING_VIEW: 'dma_monitoring_view',
  DMA_MONITORING_EXPORT: 'dma_monitoring_export',

  // Night Min Consumption
  NIGHT_MIN_CONSUMPTION_VIEW: 'night_min_consumption_view',
  NIGHT_MIN_CONSUMPTION_EXPORT: 'night_min_consumption_export',
  NIGHT_MIN_CONSUMPTION_SYNC: 'night_min_consumption_sync'
};

// Permission groups for easier management
export const PERMISSION_GROUPS = {
  DASHBOARD: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.DASHBOARD_EXPORT],
  MAP: [PERMISSIONS.MAP_VIEW],
  DEVICE: [
    PERMISSIONS.DEVICE_VIEW,
    PERMISSIONS.DEVICE_MODIFY,
    PERMISSIONS.DEVICE_EXPORT,
    PERMISSIONS.DEVICE_IMPORT
  ],
  COMMAND: [
    PERMISSIONS.COMMAND_HISTORY_VIEW,
    PERMISSIONS.COMMAND_SET_BASIC,
    PERMISSIONS.COMMAND_SET_ADVANCED,
    PERMISSIONS.COMMAND_VIEW_RESPONSE,
    PERMISSIONS.COMMAND_EXPORT,
    PERMISSIONS.COMMAND_VIEW_METER_READING_HISTORY,
    PERMISSIONS.COMMAND_SET_METER_READING,
    PERMISSIONS.COMMAND_SET_RETRANSMISSION_TIMES,
    PERMISSIONS.COMMAND_SET_SERVER_ADDRESS,
    PERMISSIONS.COMMAND_SET_DENSE_DATA_CYCLE,
    PERMISSIONS.COMMAND_SET_VALVE_CONTROL,
    PERMISSIONS.COMMAND_SET_TIME_ZONE,
    PERMISSIONS.COMMAND_SET_ALARM_REPORTING,
    PERMISSIONS.COMMAND_IMPORT,
    PERMISSIONS.CONSUMPTION_ADJUST
  ],
  REPLACEMENT: [
    PERMISSIONS.REPLACEMENT_VIEW,
    PERMISSIONS.REPLACEMENT_MODIFY,
    PERMISSIONS.REPLACEMENT_EXPORT,
    PERMISSIONS.REPLACEMENT_IMPORT
  ],
  ORIGINAL_DATA: [PERMISSIONS.ORIGINAL_DATA_VIEW, PERMISSIONS.ORIGINAL_DATA_EXPORT],
  CONSUMPTION: [
    PERMISSIONS.CONSUMPTION_VIEW,
    PERMISSIONS.CONSUMPTION_CALCULATE,
    PERMISSIONS.CONSUMPTION_MANUAL_IMPORT,
    PERMISSIONS.CONSUMPTION_EXPORT
  ],
  DAILY_CONSUMPTION: [PERMISSIONS.DAILY_CONSUMPTION_VIEW, PERMISSIONS.DAILY_CONSUMPTION_EXPORT],

  WARNING: [
    PERMISSIONS.WARNING_VIEW,
    PERMISSIONS.WARNING_UPDATE_STATUS,
    PERMISSIONS.WARNING_EXPORT
  ],
  CUSTOMER: [
    PERMISSIONS.CUSTOMER_VIEW,
    PERMISSIONS.CUSTOMER_MODIFY,
    PERMISSIONS.CUSTOMER_EXPORT,
    PERMISSIONS.CUSTOMER_IMPORT
  ],
  CONFIG: [
    PERMISSIONS.CONFIG_VIEW,
    PERMISSIONS.CONFIG_MODIFY,
    PERMISSIONS.CONFIG_EXPORT,
    PERMISSIONS.CONFIG_IMPORT
  ],
  LEAK_DETECTOR: [PERMISSIONS.LEAK_DETECTOR_VIEW, PERMISSIONS.LEAK_DETECTOR_EXPORT],
  MASTER_DATA: [PERMISSIONS.MASTER_DATA_VIEW, PERMISSIONS.MASTER_DATA_MODIFY],
  USER_MANAGEMENT: [PERMISSIONS.USER_VIEW, PERMISSIONS.USER_MODIFY, PERMISSIONS.AUTH_MANAGE],
  FAVORITE_FILTER: [
    PERMISSIONS.FAVORITE_FILTER_VIEW,
    PERMISSIONS.FAVORITE_FILTER_CREATE,
    PERMISSIONS.FAVORITE_FILTER_CREATE_DEVICE,
    PERMISSIONS.FAVORITE_FILTER_IMPORT
  ],
  DMA: [
    PERMISSIONS.DMA_VIEW,
    PERMISSIONS.DMA_CREATE,
    PERMISSIONS.DMA_MODIFY,
    PERMISSIONS.DMA_DELETE
  ],
  DMA_MONITORING: [PERMISSIONS.DMA_MONITORING_VIEW, PERMISSIONS.DMA_MONITORING_EXPORT]
};

export const usePermission = () => {
  const authData = JSON.parse(loadFromLocalStorage(contantAuthentication.DATA_AUTH));
  const user = authData?.user || null;
  const userPermissions = user?.permissions || [];
  const userRoles = user?.roleDetails || user?.roles || [];

  // Convert permissions array to a Set for faster lookup
  const permissionSet = useMemo(() => {
    return new Set(userPermissions.map((p) => (typeof p === 'string' ? p : p.name)));
  }, [userPermissions]);

  // Convert roles array to a Set for faster lookup
  const roleSet = useMemo(() => {
    return new Set(userRoles.map((r) => (typeof r === 'string' ? r : r.name)));
  }, [userRoles]);

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission name to check
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!user) return false;

    // Super admin has all permissions
    if (roleSet.has('SUPER_ADMIN')) return true;

    return permissionSet.has(permission);
  };

  /**
   * Check if user has any of the specified permissions
   * @param {string[]} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (!user) return false;
    if (roleSet.has('SUPER_ADMIN')) return true;

    return permissions.some((permission) => permissionSet.has(permission));
  };

  /**
   * Check if user has all specified permissions
   * @param {string[]} permissions - Array of permission names
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (!user) return false;
    if (roleSet.has('SUPER_ADMIN')) return true;

    return permissions.every((permission) => permissionSet.has(permission));
  };

  /**
   * Check if user has a specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean}
   */
  const hasRole = (roleName) => {
    if (!user) return false;
    return roleSet.has(roleName);
  };

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roleNames - Array of role names
   * @returns {boolean}
   */
  const hasAnyRole = (roleNames) => {
    if (!user) return false;
    return roleNames.some((roleName) => roleSet.has(roleName));
  };

  /**
   * Get all user permissions
   * @returns {string[]}
   */
  const getUserPermissions = () => {
    return Array.from(permissionSet);
  };

  /**
   * Get all user roles
   * @returns {string[]}
   */
  const getUserRoles = () => {
    return Array.from(roleSet);
  };

  /**
   * Check if user can access a specific module/page
   * @param {string} module - Module name (DASHBOARD, DEVICE, etc.)
   * @returns {boolean}
   */
  const canAccessModule = (module) => {
    if (!user) return false;
    if (roleSet.has('SUPER_ADMIN')) return true;

    const modulePermissions = PERMISSION_GROUPS[module] || [];
    return hasAnyPermission(modulePermissions);
  };

  // Grouped permissions by module
  const dashboard = {
    view: hasPermission(PERMISSIONS.DASHBOARD_VIEW),
    export: hasPermission(PERMISSIONS.DASHBOARD_EXPORT)
  };

  const map = {
    view: hasPermission(PERMISSIONS.MAP_VIEW)
  };

  const device = {
    view: hasPermission(PERMISSIONS.DEVICE_VIEW),
    create: hasPermission(PERMISSIONS.DEVICE_MODIFY),
    edit: hasPermission(PERMISSIONS.DEVICE_MODIFY),
    delete: hasPermission(PERMISSIONS.DEVICE_MODIFY),
    export: hasPermission(PERMISSIONS.DEVICE_EXPORT),
    import: hasPermission(PERMISSIONS.DEVICE_IMPORT)
  };

  const command = {
    viewHistory: hasPermission(PERMISSIONS.COMMAND_HISTORY_VIEW),
    setBasic: hasPermission(PERMISSIONS.COMMAND_SET_BASIC),
    setAdvanced: hasPermission(PERMISSIONS.COMMAND_SET_ADVANCED),
    viewResponse: hasPermission(PERMISSIONS.COMMAND_VIEW_RESPONSE),
    exportFull: hasPermission(PERMISSIONS.COMMAND_EXPORT),
    viewMeterHistory: hasPermission(PERMISSIONS.COMMAND_VIEW_METER_READING_HISTORY),
    setMeterReading: hasPermission(PERMISSIONS.COMMAND_SET_METER_READING),
    setRetransmissionTimes: hasPermission(PERMISSIONS.COMMAND_SET_RETRANSMISSION_TIMES),
    setServerAddress: hasPermission(PERMISSIONS.COMMAND_SET_SERVER_ADDRESS),
    setDenseDataCycle: hasPermission(PERMISSIONS.COMMAND_SET_DENSE_DATA_CYCLE),
    setValveControl: hasPermission(PERMISSIONS.COMMAND_SET_VALVE_CONTROL),
    setTimeZone: hasPermission(PERMISSIONS.COMMAND_SET_TIME_ZONE),
    setAlarmReporting: hasPermission(PERMISSIONS.COMMAND_SET_ALARM_REPORTING),
    import: hasPermission(PERMISSIONS.COMMAND_IMPORT)
  };

  const replacement = {
    view: hasPermission(PERMISSIONS.REPLACEMENT_VIEW),
    create: hasPermission(PERMISSIONS.REPLACEMENT_MODIFY),
    edit: hasPermission(PERMISSIONS.REPLACEMENT_MODIFY),
    delete: hasPermission(PERMISSIONS.REPLACEMENT_MODIFY),
    export: hasPermission(PERMISSIONS.REPLACEMENT_EXPORT),
    import: hasPermission(PERMISSIONS.REPLACEMENT_IMPORT)
  };

  const originalData = {
    view: hasPermission(PERMISSIONS.ORIGINAL_DATA_VIEW),
    export: hasPermission(PERMISSIONS.ORIGINAL_DATA_EXPORT)
  };

  const consumption = {
    view: hasPermission(PERMISSIONS.CONSUMPTION_VIEW),
    calculate: hasPermission(PERMISSIONS.CONSUMPTION_CALCULATE),
    manualImport: hasPermission(PERMISSIONS.CONSUMPTION_MANUAL_IMPORT),
    export: hasPermission(PERMISSIONS.CONSUMPTION_EXPORT),
    adjustConsumption: hasPermission(PERMISSIONS.CONSUMPTION_ADJUST)
  };

  const dailyConsumption = {
    view: hasPermission(PERMISSIONS.DAILY_CONSUMPTION_VIEW),
    export: hasPermission(PERMISSIONS.DAILY_CONSUMPTION_EXPORT)
  };

  const warning = {
    view: hasPermission(PERMISSIONS.WARNING_VIEW),
    updateStatus: hasPermission(PERMISSIONS.WARNING_UPDATE_STATUS),
    export: hasPermission(PERMISSIONS.WARNING_EXPORT)
  };

  const customer = {
    view: hasPermission(PERMISSIONS.CUSTOMER_VIEW),
    create: hasPermission(PERMISSIONS.CUSTOMER_MODIFY),
    edit: hasPermission(PERMISSIONS.CUSTOMER_MODIFY),
    delete: hasPermission(PERMISSIONS.CUSTOMER_MODIFY),
    export: hasPermission(PERMISSIONS.CUSTOMER_EXPORT),
    import: hasPermission(PERMISSIONS.CUSTOMER_IMPORT)
  };

  const config = {
    view: hasPermission(PERMISSIONS.CONFIG_VIEW),
    create: hasPermission(PERMISSIONS.CONFIG_MODIFY),
    edit: hasPermission(PERMISSIONS.CONFIG_MODIFY),
    delete: hasPermission(PERMISSIONS.CONFIG_MODIFY),
    export: hasPermission(PERMISSIONS.CONFIG_EXPORT),
    import: hasPermission(PERMISSIONS.CONFIG_IMPORT)
  };

  const leakDetector = {
    view: hasPermission(PERMISSIONS.LEAK_DETECTOR_VIEW),
    export: hasPermission(PERMISSIONS.LEAK_DETECTOR_EXPORT)
  };

  const masterData = {
    view: hasPermission(PERMISSIONS.MASTER_DATA_VIEW),
    create: hasPermission(PERMISSIONS.MASTER_DATA_MODIFY),
    edit: hasPermission(PERMISSIONS.MASTER_DATA_MODIFY),
    delete: hasPermission(PERMISSIONS.MASTER_DATA_MODIFY)
  };

  const userManagement = {
    view: hasPermission(PERMISSIONS.USER_VIEW),
    create: hasPermission(PERMISSIONS.USER_MODIFY),
    edit: hasPermission(PERMISSIONS.USER_MODIFY),
    delete: hasPermission(PERMISSIONS.USER_MODIFY),
    assignRoles: hasPermission(PERMISSIONS.USER_MODIFY),
    manageAuth: hasPermission(PERMISSIONS.AUTH_MANAGE),
    manageRoles: hasPermission(PERMISSIONS.AUTH_MANAGE),
    managePermissions: hasPermission(PERMISSIONS.MANAGE_PERMISSIONS)
  };

  const consumptionMonitoring = {
    view: hasPermission(PERMISSIONS.CONSUMPTION_MONITORING_VIEW),
    export: hasPermission(PERMISSIONS.CONSUMPTION_MONITORING_EXPORT)
  };

  const favoriteFilter = {
    view: hasPermission(PERMISSIONS.FAVORITE_FILTER_VIEW),
    create: hasPermission(PERMISSIONS.FAVORITE_FILTER_CREATE),
    createDevice: hasPermission(PERMISSIONS.FAVORITE_FILTER_CREATE_DEVICE),
    import: hasPermission(PERMISSIONS.FAVORITE_FILTER_IMPORT)
  };

  const dma = {
    view: hasPermission(PERMISSIONS.DMA_VIEW),
    create: hasPermission(PERMISSIONS.DMA_CREATE),
    edit: hasPermission(PERMISSIONS.DMA_MODIFY),
    delete: hasPermission(PERMISSIONS.DMA_DELETE)
  };

  const dmaMonitoring = {
    view: hasPermission(PERMISSIONS.DMA_MONITORING_VIEW),
    export: hasPermission(PERMISSIONS.DMA_MONITORING_EXPORT)
  };

  const nightMinConsumption = {
    view: hasPermission(PERMISSIONS.NIGHT_MIN_CONSUMPTION_VIEW),
    export: hasPermission(PERMISSIONS.NIGHT_MIN_CONSUMPTION_EXPORT),
    sync: hasPermission(PERMISSIONS.NIGHT_MIN_CONSUMPTION_SYNC)
  };

  return {
    // Grouped permissions by module
    dashboard,
    map,
    device,
    command,
    replacement,
    originalData,
    consumption,
    dailyConsumption,
    warning,
    customer,
    config,
    leakDetector,
    masterData,
    userManagement,
    consumptionMonitoring,
    favoriteFilter,
    dma,
    dmaMonitoring,
    nightMinConsumption,

    // Permission check functions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Role checks
    hasRole,
    hasAnyRole,

    // Getters
    getUserPermissions,
    getUserRoles,

    // Module access
    canAccessModule,

    // Data
    user,
    permissions: userPermissions,
    roles: userRoles,

    // Computed flags for common checks
    isSuperAdmin: hasRole('SUPER_ADMIN'),
    isAdmin: hasAnyRole(['SUPER_ADMIN', 'ADMIN']),
    canManageUsers: hasPermission(PERMISSIONS.USER_MODIFY),
    canManageAuth: hasPermission(PERMISSIONS.AUTH_MANAGE)
  };
};
